import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { HardDrive, FolderOpen, Download, Plus, Trash2, Pencil, RefreshCw, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { getFolderHandle, saveFolderHandle, clearFolderHandle } from '../lib/backupFolderStore';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const FS_API_SUPPORTED = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
const IS_SECURE_CONTEXT = typeof window !== 'undefined' && window.isSecureContext;
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export const DatabaseBackupSettings = () => {
  const [schedules, setSchedules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [dueList, setDueList] = useState([]);
  const [folderHandle, setFolderHandle] = useState(null);
  const [folderPermission, setFolderPermission] = useState('none'); // none | prompt | granted
  const [runningNow, setRunningNow] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [form, setForm] = useState({ name: '', frequency: 'daily', time_of_day: '23:00', day_of_week: 0, day_of_month: 1, retention_count: 10, is_active: true });

  const loadSchedules = useCallback(async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/settings/backup/schedules`, { headers: authHeaders() });
      setSchedules(res.data || []);
    } catch (e) { /* silent - user may not be admin */ }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/settings/backup/logs`, { headers: authHeaders() });
      setLogs(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadDue = useCallback(async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/settings/backup/due`, { headers: authHeaders() });
      setDueList(res.data || []);
      return res.data || [];
    } catch (e) {
      return [];
    }
  }, []);

  // Restore the remembered folder handle (if any) and check if permission is still valid
  useEffect(() => {
    (async () => {
      if (!FS_API_SUPPORTED) return;
      const handle = await getFolderHandle();
      if (handle) {
        setFolderHandle(handle);
        try {
          const perm = await handle.queryPermission({ mode: 'readwrite' });
          setFolderPermission(perm);
        } catch (e) {
          setFolderPermission('prompt');
        }
      }
    })();
    loadSchedules();
    loadLogs();
    loadDue();
  }, [loadSchedules, loadLogs, loadDue]);

  const cleanupOldBackups = async (handle, retentionCount) => {
    try {
      const files = [];
      for await (const [name, entry] of handle.entries()) {
        if (entry.kind === 'file' && name.startsWith('garage-backup-') && name.endsWith('.gz')) {
          files.push(name);
        }
      }
      files.sort(); // filenames are timestamp-ordered, so this sorts oldest -> newest
      const toDelete = files.slice(0, Math.max(0, files.length - retentionCount));
      for (const name of toDelete) {
        await handle.removeEntry(name);
      }
    } catch (e) {
      console.error('Retention cleanup failed:', e);
    }
  };

  const runBackup = async (scheduleId, triggeredBy, retentionCount = 10) => {
    setRunningNow(true);
    try {
      const params = new URLSearchParams();
      if (scheduleId) params.set('schedule_id', scheduleId);
      params.set('triggered_by', triggeredBy || 'manual');
      const res = await fetch(`${backendUrl}/api/settings/backup/run?${params.toString()}`, {
        method: 'POST',
        headers: authHeaders()
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Backup failed');
      }
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `garage-backup-${Date.now()}.gz`;
      const blob = await res.blob();

      if (folderHandle && folderPermission === 'granted') {
        const fileHandle = await folderHandle.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        await cleanupOldBackups(folderHandle, retentionCount);
        toast.success(`Backup saved to "${folderHandle.name}" folder`);
      } else {
        // Fallback: normal browser download (Save As / Downloads folder)
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('Backup downloaded');
      }
      await loadLogs();
      await loadDue();
    } catch (e) {
      console.error('Backup failed:', e);
      toast.error(e.message || 'Backup failed');
    } finally {
      setRunningNow(false);
    }
  };

  // Auto-run any due schedules once the folder is connected and permission is granted
  useEffect(() => {
    if (!folderHandle || folderPermission !== 'granted') return;
    (async () => {
      const due = await loadDue();
      for (const d of due.filter(x => x.is_due)) {
        const sched = schedules.find(s => s.id === d.schedule_id);
        await runBackup(d.schedule_id, d.name, sched?.retention_count || 10);
      }
    })();
  }, [folderHandle, folderPermission, schedules]);

  const connectFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
      await saveFolderHandle(handle);
      setFolderHandle(handle);
      setFolderPermission('granted');
      toast.success(`Connected to folder "${handle.name}"`);
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Could not connect to folder');
    }
  };

  const reconnectFolder = async () => {
    try {
      const perm = await folderHandle.requestPermission({ mode: 'readwrite' });
      setFolderPermission(perm);
      if (perm === 'granted') toast.success('Folder access restored');
    } catch (e) {
      toast.error('Could not restore folder access');
    }
  };

  const disconnectFolder = async () => {
    await clearFolderHandle();
    setFolderHandle(null);
    setFolderPermission('none');
    toast.success('Backup folder disconnected');
  };

  const openCreateDialog = () => {
    setEditingSchedule(null);
    setForm({ name: '', frequency: 'daily', time_of_day: '23:00', day_of_week: 0, day_of_month: 1, retention_count: 10, is_active: true });
    setShowScheduleDialog(true);
  };

  const openEditDialog = (s) => {
    setEditingSchedule(s);
    setForm({
      name: s.name, frequency: s.frequency, time_of_day: s.time_of_day,
      day_of_week: s.day_of_week ?? 0, day_of_month: s.day_of_month ?? 1,
      retention_count: s.retention_count, is_active: s.is_active
    });
    setShowScheduleDialog(true);
  };

  const saveSchedule = async () => {
    if (!form.name.trim()) {
      toast.error('Schedule name is required');
      return;
    }
    const payload = {
      name: form.name.trim(),
      frequency: form.frequency,
      time_of_day: form.time_of_day,
      day_of_week: form.frequency === 'weekly' ? parseInt(form.day_of_week) : null,
      day_of_month: form.frequency === 'monthly' ? parseInt(form.day_of_month) : null,
      retention_count: parseInt(form.retention_count) || 10,
      is_active: form.is_active
    };
    try {
      if (editingSchedule) {
        await axios.put(`${backendUrl}/api/settings/backup/schedules/${editingSchedule.id}`, payload, { headers: authHeaders() });
        toast.success('Schedule updated');
      } else {
        await axios.post(`${backendUrl}/api/settings/backup/schedules`, payload, { headers: authHeaders() });
        toast.success('Schedule created');
      }
      setShowScheduleDialog(false);
      await loadSchedules();
      await loadDue();
    } catch (e) {
      toast.error('Failed to save schedule');
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this backup schedule?')) return;
    try {
      await axios.delete(`${backendUrl}/api/settings/backup/schedules/${id}`, { headers: authHeaders() });
      toast.success('Schedule deleted');
      await loadSchedules();
      await loadDue();
    } catch (e) {
      toast.error('Failed to delete schedule');
    }
  };

  const scheduleSummary = (s) => {
    if (s.frequency === 'daily') return `Daily at ${s.time_of_day}`;
    if (s.frequency === 'weekly') return `Weekly on ${DAYS_OF_WEEK[s.day_of_week ?? 0]} at ${s.time_of_day}`;
    return `Monthly on day ${s.day_of_month} at ${s.time_of_day}`;
  };

  return (
    <Card data-testid="database-backup-settings">
      <CardHeader>
        <CardTitle className="flex items-center">
          <HardDrive className="h-5 w-5 mr-2 text-slate-600" />
          Database Backup
        </CardTitle>
        <p className="text-sm text-gray-600">
          Create real MongoDB dumps and save them to a local folder or external drive. Admin only.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Folder connection */}
        <div className="p-4 rounded-lg border bg-gray-50">
          {!FS_API_SUPPORTED ? (
            <p className="text-sm text-amber-700">
              Your browser doesn't support saving directly to a folder (this needs Chrome or Edge). "Backup Now" will still work using your browser's normal Save-As download.
            </p>
          ) : folderHandle ? (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-sm" data-testid="connected-folder-name">Connected: "{folderHandle.name}"</p>
                  <p className="text-xs text-gray-500">
                    {folderPermission === 'granted' ? 'Ready — scheduled backups will auto-save here while this tab is open' : 'Permission needs to be reconfirmed'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {folderPermission !== 'granted' && (
                  <Button size="sm" variant="outline" onClick={reconnectFolder} data-testid="reconnect-folder-btn">
                    Reconnect
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={disconnectFolder} data-testid="disconnect-folder-btn">
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-gray-600">No backup folder connected yet — choose a folder on this PC or an external drive.</p>
              <Button size="sm" onClick={connectFolder} data-testid="connect-folder-btn">
                <FolderOpen className="h-4 w-4 mr-1" /> Choose Backup Folder
              </Button>
            </div>
          )}
        </div>

        {/* Manual backup */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-sm">Manual Backup</p>
            <p className="text-xs text-gray-500">Generate a backup right now, on demand.</p>
          </div>
          <Button onClick={() => runBackup(null, 'manual')} disabled={runningNow} data-testid="backup-now-btn">
            <Download className="h-4 w-4 mr-1" />
            {runningNow ? 'Backing up...' : 'Backup Now'}
          </Button>
        </div>

        {/* Schedules */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-sm">Automatic Schedules</p>
            <Button size="sm" variant="outline" onClick={openCreateDialog} data-testid="add-backup-schedule-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Schedule
            </Button>
          </div>
          {schedules.length === 0 ? (
            <p className="text-sm text-gray-500">No schedules yet. Add one to automate backups.</p>
          ) : (
            <div className="space-y-2">
              {schedules.map((s) => {
                const due = dueList.find(d => d.schedule_id === s.id);
                return (
                  <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`backup-schedule-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{s.name} {!s.is_active && <span className="text-xs text-gray-400">(paused)</span>}</p>
                        <p className="text-xs text-gray-500">
                          {scheduleSummary(s)} · keeps last {s.retention_count}
                          {due?.is_due && <span className="text-amber-600 font-medium"> · due now</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEditDialog(s)} data-testid={`edit-schedule-${s.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteSchedule(s.id)} data-testid={`delete-schedule-${s.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">
            Note: automatic backups only run while this Settings page is open in Chrome/Edge with the folder connected. If a scheduled time is missed, it runs on your next visit.
          </p>
        </div>

        {/* Recent backups */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-sm">Recent Backups</p>
            <Button size="icon" variant="ghost" onClick={() => { loadLogs(); loadDue(); }} data-testid="refresh-backup-logs-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {logs.length === 0 ? (
            <p className="text-sm text-gray-500">No backups yet.</p>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between text-sm py-2 px-3 border-b last:border-0" data-testid={`backup-log-${log.id}`}>
                  <div className="flex items-center gap-2">
                    {log.status === 'success' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span>{log.filename}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{formatBytes(log.size_bytes)}</span>
                    <span>{log.triggered_by}</span>
                    <span>{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Add/Edit Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent data-testid="backup-schedule-dialog">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Edit Backup Schedule' : 'New Backup Schedule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Schedule Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Nightly Backup"
                data-testid="schedule-name-input"
              />
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.frequency === 'weekly' && (
              <div>
                <Label>Day of Week</Label>
                <Select value={String(form.day_of_week)} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                  <SelectTrigger data-testid="schedule-day-of-week-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.frequency === 'monthly' && (
              <div>
                <Label>Day of Month</Label>
                <Input
                  type="number" min="1" max="28"
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                  data-testid="schedule-day-of-month-input"
                />
              </div>
            )}
            <div>
              <Label>Time of Day</Label>
              <Input
                type="time"
                value={form.time_of_day}
                onChange={(e) => setForm({ ...form, time_of_day: e.target.value })}
                data-testid="schedule-time-input"
              />
            </div>
            <div>
              <Label>Keep Last N Backups</Label>
              <Input
                type="number" min="1" max="365"
                value={form.retention_count}
                onChange={(e) => setForm({ ...form, retention_count: e.target.value })}
                data-testid="schedule-retention-input"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="schedule-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={saveSchedule} data-testid="save-schedule-btn">Save Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DatabaseBackupSettings;
